import { useTheme } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { trpc } from '../../../../../../trpc/react';
import { usePlanContext } from '../../../context/PlanContext';
import { useCalendarContext } from '../../context/CalendarContext';
import { CustomDay } from './CustomDay';
import { MiniCalendarHeader } from './MiniCalendarHeader';

export function MiniCalendar() {
  const { miniCalendarDate, minDate, maxDate, navigateToDate } =
    useCalendarContext();
  const { teamId, selectedAthlete, showCanceled } = usePlanContext();
  const { palette } = useTheme();

  const dateParam = miniCalendarDate.startOf('month').toISODate() ?? '';

  const { data } = trpc.plan.workoutDates.useQuery(
    {
      athleteId: selectedAthlete?.id ?? null,
      date: dateParam,
    },
    {
      enabled: !!dateParam,
      staleTime: 30_000, // Cache for 30 seconds
    },
  );

  const workoutDates = useMemo(() => {
    const datedWorkouts = data?.workouts?.filter((w) => w.date !== null) ?? [];
    const filteredWorkouts = showCanceled
      ? datedWorkouts
      : datedWorkouts.filter((w) => w.status !== 'canceled');
    // TODO: Use trackId for color-coded dots in the future
    return new Set(filteredWorkouts.map((w) => w.date));
  }, [data?.workouts, showCanceled]);

  // When clicking a day, navigate the main calendar to that day's month
  const handleDayClick = useCallback(
    (date: DateTime | null) => {
      if (date) {
        navigateToDate(date);
      }
    },
    [navigateToDate],
  );

  return (
    <DateCalendar
      value={miniCalendarDate}
      onChange={handleDayClick}
      minDate={minDate}
      maxDate={maxDate}
      showDaysOutsideCurrentMonth
      fixedWeekNumber={6}
      slots={{
        calendarHeader: (props) => (
          <MiniCalendarHeader {...props} minDate={minDate} maxDate={maxDate} />
        ),
        day: (dayProps) => {
          const dateStr = dayProps.day.toISODate();
          const hasWorkout = dateStr ? workoutDates.has(dateStr) : false;
          return <CustomDay {...dayProps} hasWorkout={hasWorkout} />;
        },
      }}
      sx={{
        width: '100%',
        '& .MuiPickersDay-root': {
          fontWeight: 400,
          fontSize: '14px',
        },
        '& .MuiDayCalendar-weekDayLabel': {
          fontWeight: 600,
          fontSize: '14px',
          color: palette.text.primary,
        },
        '& .MuiPickersDay-dayOutsideMonth': {
          color: palette.text.disabled,
        },
      }}
    />
  );
}
